import Image from 'next/image';
import React from 'react';
import SectionHeadline from '../ui/elements/SectionHeadline';
import { imageUrlBuilder } from '@/utils/helpers';
import styles from './styles.module.css';

interface Logo {
  name: string;
  // The home page query resolves the asset URL in GROQ (api.ts). Page-builder
  // pages fetch the `page` document unprojected, so the raw image object is all
  // we get and the URL has to be built here.
  logoUrl?: string;
  logo?: { asset?: { _ref?: string; _id?: string }; crop?: unknown; hotspot?: unknown };
  maxHeight?: number;
}

// The whole image object goes to the builder, not just `asset` -- `clientLogo`
// enables the hotspot tool, and dropping crop/hotspot would silently ignore the
// framing an editor set in the Studio.
const resolveLogoUrl = (logo: Logo): string | undefined =>
  logo.logoUrl ??
  (logo.logo?.asset
    ? imageUrlBuilder
        .image(logo.logo as Parameters<typeof imageUrlBuilder.image>[0])
        .width(240)
        .auto('format')
        .quality(80)
        .url()
    : undefined);

// Sanity serves SVGs untouched -- the image pipeline cannot rasterise them, and
// Next's optimizer rejects them outright unless `dangerouslyAllowSVG` is set.
// Logos are the one place SVG uploads are expected, so those bypass the
// optimizer instead of failing to render.
const isSvg = (logo: Logo, src: string): boolean =>
  (logo.logo?.asset?._ref ?? logo.logo?.asset?._id ?? '').endsWith('-svg') || src.split('?')[0].endsWith('.svg');

interface ClientLogoBarProps {
  heading?: string;
  subtitle?: string;
  logos: Logo[];
}

// FOX2-199. Each logo sits in a fractional-width slot inside a wrapping flex
// row -- 2 per row, 3 from `sm`, 5 from `lg` -- and `justify-center` centres
// whatever a row actually holds. The slots keep the fluid, evenly spread
// spacing a grid gave, but a partial row (four logos on /services/healthcare)
// no longer strands its empty slots on one side.
const ClientLogoBar: React.FC<ClientLogoBarProps> = ({ heading, subtitle, logos }) => {
  const resolvedLogos = (logos || [])
    .map((logo) => ({ ...logo, src: resolveLogoUrl(logo) }))
    .filter((logo): logo is Logo & { src: string } => Boolean(logo.src));

  if (resolvedLogos.length === 0) return null;

  return (
    <section className="bg-white" style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        {heading && (
          <SectionHeadline lg={20} md={20} sm={18} xs={16} style={{ textAlign: 'center', marginBottom: '4px' }}>
            {heading}
          </SectionHeadline>
        )}
        {subtitle && (
          <p className="text-center text-base text-gray-500 max-w-2xl mx-auto mb-2 leading-relaxed">{subtitle}</p>
        )}
        <div className="mx-auto w-20 border-t border-gray-200 mb-4" />
        <div className="mx-auto mt-2 flex max-w-sm flex-wrap items-center justify-center gap-y-8 sm:max-w-xl lg:max-w-none">
          {resolvedLogos.map((item, index) => (
            <div key={index} className="flex w-1/2 justify-center sm:w-1/3 lg:w-1/5">
              <Image
                src={item.src}
                alt={item.name}
                width={120}
                height={68}
                unoptimized={isSvg(item, item.src)}
                style={item.maxHeight ? { maxHeight: item.maxHeight } : undefined}
                className={`object-contain ${styles.logoImg}`}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ClientLogoBar;
